import type {
  ApiResponseBody,
  ApiSearchQuery,
} from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetValueListQuery = ApiSearchQuery<
  '/v2/product-central/values',
  'get'
>;
export type GetValueListResponse = ApiResponseBody<
  '/v2/product-central/values',
  'get'
>;
export type ValueListItem = GetValueListResponse['items'][0];

export type ValueOrderBy = NonNullable<GetValueListQuery>['orderBy'];

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetValueCountQuery = ApiSearchQuery<
  '/v2/product-central/values/count',
  'get'
>;
export type GetValueCountResponse = ApiResponseBody<
  '/v2/product-central/values/count',
  'get'
>;

// 注: Post (作成) / Detail (詳細取得) / Patch (更新) の型は現状の BE schema に対応
// endpoint が無いため省略。追加・編集・詳細実装時に BE 側で POST/PATCH/GET {id} を
// 追加した後で復活させる。

// ---------------------------------------------------------------------------
// ValueType / ValueLayer enums (BE: SEED / HYPOTHESIS / PROMISE と
//   COMPANY_WIDE / EXECUTIVE / MANAGER / OPERATOR を反映)
//
// 旧名称「ValueStatus」は ValueType にリネームされた（SEED/HYPOTHESIS/PROMISE は
// 「価値のフェーズ＝種別」であり、運用上の有効/停止フラグとは別軸のため）。
// 新規 ValueStatus（ACTIVE/INACTIVE）は BE 側のみで保持し、当面 FE には型を持たない。
// ---------------------------------------------------------------------------

export const ValueType = {
  SEED: 'SEED',
  HYPOTHESIS: 'HYPOTHESIS',
  HYPOTHESIS_VERIFIED: 'HYPOTHESIS_VERIFIED',
  PROMISE: 'PROMISE',
} as const;
export type ValueType = (typeof ValueType)[keyof typeof ValueType];

const VALUE_TYPE_VALUES: ReadonlySet<string> = new Set(
  Object.values(ValueType),
);
export function isValueType(
  value: string | null | undefined,
): value is ValueType {
  return value != null && VALUE_TYPE_VALUES.has(value);
}

/** 種別フィルタ用。ドメイン値 + "ALL"。 */
export type ValueTypeFilter = ValueType | 'ALL';

export const ValueLayer = {
  COMPANY_WIDE: 'COMPANY_WIDE',
  EXECUTIVE: 'EXECUTIVE',
  MANAGER: 'MANAGER',
  OPERATOR: 'OPERATOR',
} as const;
export type ValueLayer = (typeof ValueLayer)[keyof typeof ValueLayer];

const VALUE_LAYER_VALUES: ReadonlySet<string> = new Set(
  Object.values(ValueLayer),
);
export function isValueLayer(
  value: string | null | undefined,
): value is ValueLayer {
  return value != null && VALUE_LAYER_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// ValueProposition enum (BE: POD / POP / POF / UNKNOWN)
//
// PoD (Point of Difference) / PoP (Point of Parity) / PoF (Point of Failure) /
// Unknown。Value の「価値提案」軸を表す。detail / list / post-create の
// response・request に必須フィールドとして含まれる。
// ---------------------------------------------------------------------------

export const ValueProposition = {
  POD: 'POD',
  POP: 'POP',
  POF: 'POF',
  UNKNOWN: 'UNKNOWN',
} as const;
export type ValueProposition =
  (typeof ValueProposition)[keyof typeof ValueProposition];

const VALUE_PROPOSITION_VALUES: ReadonlySet<string> = new Set(
  Object.values(ValueProposition),
);
export function isValueProposition(
  value: string | null | undefined,
): value is ValueProposition {
  return value != null && VALUE_PROPOSITION_VALUES.has(value);
}
